import React, { useState, useEffect, useCallback } from 'react';
import adminAttendanceService from '../../firebase/adminAttendanceService';
import AttendanceStats from './AttendanceStats';
import AttendanceFilters from './AttendanceFilters';
import AttendanceRecordCard from './AttendanceRecordCard';
import AttendanceTable from './AttendanceTable';
import AttendancePhotoModal from './AttendancePhotoModal';
import CSVAttendanceImport from './CSVAttendanceImport';
import ManualAttendanceForm from './ManualAttendanceForm';
import './AttendanceMonitoring.css';
import './AttendanceTableStyles.css';

/**
 * AttendanceMonitoring Component
 * Main component for attendance monitoring dashboard
 */
const AttendanceMonitoring = ({ dateRange, onDateRangeChange }) => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendanceUnsubscribe, setAttendanceUnsubscribe] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    employeeId: 'all',
    status: 'all',
    type: 'all',
    location: 'all',
    validationScore: { min: 0, max: 100 },
    searchTerm: ''
  });

  // Photo modal states
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(20);

  // View states
  const [viewFormat, setViewFormat] = useState('table'); // 'table' or 'cards'

  // Modal states for manual attendance features
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);

  // Setup attendance records subscription
  const setupAttendanceSubscription = useCallback(() => {
    console.log('🔄 Setting up attendance subscription with filters:', { dateRange, filters });

    setLoading(true);
    setError(null);

    // Build subscription options
    const options = {
      limitCount: 500 // Reasonable limit for admin dashboard
    };

    // Add date range
    if (dateRange.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      options.endDate = dateRange.endDate;
    }

    // Add filters (only non-'all' values)
    if (filters.employeeId !== 'all') {
      options.employeeId = filters.employeeId;
    }
    if (filters.status !== 'all') {
      options.status = filters.status;
    }
    if (filters.type !== 'all') {
      options.type = filters.type;
    }
    if (filters.location !== 'all') {
      options.location = filters.location;
    }

    // Setup subscription
    const unsubscribe = adminAttendanceService.subscribeToAttendanceRecords(
      (records) => {
        console.log('📥 Received attendance records:', records.length);
        setAttendanceRecords(records);
        setLoading(false);
      },
      options
    );

    return unsubscribe;
  }, [dateRange, filters]);

  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      
      const options = {};
      if (dateRange.startDate) {
        options.startDate = dateRange.startDate;
      }
      if (dateRange.endDate) {
        options.endDate = dateRange.endDate;
      }

      // Add active filters to stats calculation
      if (filters.employeeId !== 'all') {
        options.employeeId = filters.employeeId;
      }
      if (filters.status !== 'all') {
        options.status = filters.status;
      }
      if (filters.type !== 'all') {
        options.type = filters.type;
      }
      if (filters.location !== 'all') {
        options.location = filters.location;
      }

      const result = await adminAttendanceService.getAttendanceStats(options);
      if (result.success) {
        setStats(result.stats);
      } else {
        console.error('Failed to load stats:', result.error);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [dateRange, filters]);

  // Apply client-side filters (for validation score and search)
  useEffect(() => {
    let filtered = [...attendanceRecords];

    // Apply validation score filter
    if (filters.validationScore && (filters.validationScore.min > 0 || filters.validationScore.max < 100)) {
      filtered = filtered.filter(record => {
        const score = record.validationResults?.overallScore || 0;
        return score >= filters.validationScore.min && score <= filters.validationScore.max;
      });
    }

    // Apply search filter
    if (filters.searchTerm && filters.searchTerm.trim()) {
      const searchLower = filters.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(record => {
        return (
          record.employeeName?.toLowerCase().includes(searchLower) ||
          record.notes?.toLowerCase().includes(searchLower) ||
          record.adminNotes?.toLowerCase().includes(searchLower) ||
          record.employeeId?.toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredRecords(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [attendanceRecords, filters.validationScore, filters.searchTerm]);

  // Setup subscription when component mounts or dependencies change
  useEffect(() => {
    // Cleanup previous subscription
    if (attendanceUnsubscribe) {
      attendanceUnsubscribe();
    }

    // Setup new subscription
    const unsubscribe = setupAttendanceSubscription();
    setAttendanceUnsubscribe(() => unsubscribe);
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [setupAttendanceSubscription]);

  // Load stats when dependencies change
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    console.log('🔍 Filters changed:', newFilters);
    setFilters(newFilters);
  };

  // Handle status update
  const handleStatusUpdate = (recordId, newStatus) => {
    console.log('✅ Status updated:', { recordId, newStatus });
    // The real-time subscription will automatically update the records
    // Refresh stats to reflect the change
    loadStats();
  };

  // Handle photo view
  const handlePhotoView = (photoUrl, record) => {
    setSelectedPhoto(photoUrl);
    setSelectedRecord(record);
    setPhotoModalOpen(true);
  };

  // Handle photo modal close
  const handlePhotoModalClose = () => {
    setPhotoModalOpen(false);
    setSelectedPhoto(null);
    setSelectedRecord(null);
  };

  // Handle export
  const handleExport = async () => {
    try {
      const options = {};
      if (dateRange.startDate) {
        options.startDate = dateRange.startDate;
      }
      if (dateRange.endDate) {
        options.endDate = dateRange.endDate;
      }

      // Add active filters
      if (filters.employeeId !== 'all') {
        options.employeeId = filters.employeeId;
      }
      if (filters.status !== 'all') {
        options.status = filters.status;
      }
      if (filters.type !== 'all') {
        options.type = filters.type;
      }
      if (filters.location !== 'all') {
        options.location = filters.location;
      }

      const result = await adminAttendanceService.exportAttendanceData(options);
      if (result.success) {
        // Create and download CSV file
        const csvContent = generateCSV(result.data.records);
        downloadCSV(csvContent, `attendance-export-${new Date().toISOString().split('T')[0]}.csv`);
      } else {
        alert(`Export failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export attendance data');
    }
  };

  // Generate CSV content
  const generateCSV = (records) => {
    const headers = [
      'Employee Name',
      'Employee ID',
      'Type',
      'Status',
      'Timestamp',
      'Location Type',
      'GPS Coordinates',
      'GPS Accuracy',
      'Validation Score',
      'Photo Recency',
      'GPS Accuracy Check',
      'Location Verification',
      'Notes',
      'Admin Notes'
    ];

    const rows = records.map(record => [
      record.employeeName || 'Unknown',
      record.employeeId || record.uid,
      record.type,
      record.status,
      record.timestamp ? new Date(record.timestamp).toLocaleString('en-SG') : 'N/A',
      record.isWarehouse ? 'Warehouse' : 'Job-site',
      record.location ? `${record.location.latitude}, ${record.location.longitude}` : 'N/A',
      record.location?.accuracy ? `${record.location.accuracy}m` : 'N/A',
      record.validationResults?.overallScore || 0,
      record.validationResults?.photoRecency ? 'Pass' : 'Fail',
      record.validationResults?.gpsAccuracy ? 'Pass' : 'Fail',
      record.validationResults?.warehouseProximity ? 'Pass' : 'Fail',
      record.notes || '',
      record.adminNotes || ''
    ]);

    return [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');
  };

  // Download CSV file
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Pagination calculations
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (error) {
    return (
      <div className="attendance-monitoring">
        <div className="error-message">
          <h3>❌ Error Loading Attendance Data</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      </div>
    );
  }

  return (
    <div className="attendance-monitoring">
      {/* Header */}
      <div className="attendance-header">
        <div className="header-content">
          <h2>👥 Attendance Monitoring</h2>
          <div className="header-actions">
            <button 
              className="manual-add-btn"
              onClick={() => setShowManualForm(true)}
              title="Add single attendance record"
            >
              ➕ Add Record
            </button>
            <button 
              className="csv-import-btn"
              onClick={() => setShowCSVImport(true)}
              title="Import multiple records from CSV"
            >
              📊 CSV Import
            </button>
            <button 
              className="export-btn"
              onClick={handleExport}
              disabled={loading || filteredRecords.length === 0}
            >
              📤 Export Data
            </button>
          </div>
        </div>
        <p className="header-description">
          Monitor and manage employee attendance records with photo verification and GPS validation.
        </p>
      </div>

      {/* Statistics */}
      <AttendanceStats 
        stats={stats} 
        loading={statsLoading}
      />

      {/* Filters */}
      <AttendanceFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        loading={loading}
      />

      {/* View Format Toggle */}
      <div className="view-format-toggle">
        <button
          className={`format-btn ${viewFormat === 'table' ? 'active' : ''}`}
          onClick={() => setViewFormat('table')}
          title="Table View"
        >
          📊 Table
        </button>
        <button
          className={`format-btn ${viewFormat === 'cards' ? 'active' : ''}`}
          onClick={() => setViewFormat('cards')}
          title="Card View"
        >
          🗃️ Cards
        </button>
      </div>

      {/* Attendance Records */}
      <div className="attendance-records-section">
        <div className="records-header">
          <h3>📋 Attendance Records</h3>
          <div className="records-info">
            {loading ? (
              <span>Loading records...</span>
            ) : (
              <span>
                Showing {indexOfFirstRecord + 1}-{Math.min(indexOfLastRecord, filteredRecords.length)} of {filteredRecords.length} records
                {attendanceRecords.length !== filteredRecords.length && (
                  <span className="filtered-note"> (filtered from {attendanceRecords.length} total)</span>
                )}
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="records-loading">
            <div className="loading-spinner"></div>
            <p>Loading attendance records...</p>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="no-records">
            <div className="no-records-icon">📋</div>
            <h3>No Attendance Records Found</h3>
            <p>
              {attendanceRecords.length === 0 
                ? 'No attendance records exist for the selected date range.'
                : 'No records match the current filter criteria. Try adjusting your filters.'
              }
            </p>
          </div>
        ) : (
          <>
            {viewFormat === 'table' ? (
              /* Table View */
              <AttendanceTable
                records={currentRecords}
                onStatusUpdate={handleStatusUpdate}
                onPhotoView={handlePhotoView}
                onRefresh={loadStats}
                currentPage={currentPage}
                recordsPerPage={recordsPerPage}
                totalRecords={filteredRecords.length}
              />
            ) : (
              /* Card View */
              <div className="records-grid">
                {currentRecords.map((record) => (
                  <AttendanceRecordCard
                    key={record.id}
                    record={record}
                    onStatusUpdate={handleStatusUpdate}
                    onPhotoView={handlePhotoView}
                    onRefresh={loadStats}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </button>
                
                <div className="pagination-info">
                  <span>Page {currentPage} of {totalPages}</span>
                </div>
                
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Photo Modal */}
      <AttendancePhotoModal
        isOpen={photoModalOpen}
        photoUrl={selectedPhoto}
        attendanceRecord={selectedRecord}
        onClose={handlePhotoModalClose}
      />

      {/* CSV Import Modal */}
      {showCSVImport && (
        <CSVAttendanceImport
          onClose={() => setShowCSVImport(false)}
          onSuccess={(results) => {
            console.log('CSV Import completed:', results);
            setShowCSVImport(false);
            // Refresh the attendance data
            loadStats();
          }}
        />
      )}

      {/* Manual Attendance Form Modal */}
      {showManualForm && (
        <div className="modal-overlay">
          <ManualAttendanceForm
            onClose={() => setShowManualForm(false)}
            onSuccess={(result) => {
              console.log('Manual attendance added:', result);
              setShowManualForm(false);
              // Refresh the attendance data
              loadStats();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AttendanceMonitoring;
